import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Package, Search, Truck, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const STEPS = ["Order placed", "Confirmed — preparing your order", "Out for fulfillment", "Delivered"];

export default function OrderTracking() {
    const [params] = useSearchParams();
    const [num, setNum] = useState(params.get("order") || "");
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(false);

    const track = async (e) => {
        e?.preventDefault();
        if (!num.trim()) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/orders/track/${num.trim()}`);
            setOrder(data);
        } catch (e2) {
            setOrder(null);
            toast.error(apiError(e2, "Order not found"));
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => { if (params.get("order")) track(); /* eslint-disable-next-line */ }, []);

    const currentStep = order ? Math.max(0, STEPS.indexOf(order.tracking_status)) : 0;

    return (
        <div className="mx-auto max-w-2xl px-5 py-16">
            <div className="text-center">
                <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-banana/25 text-caramel"><Package size={30} /></div>
                <h1 className="mt-4 font-serif text-4xl font-semibold text-roast">Track your order</h1>
                <p className="mt-2 text-mocha">Enter your order number (e.g. SP-XXXXXXXX) to see the latest status.</p>
            </div>

            <form onSubmit={track} className="mt-8 flex gap-2">
                <div className="relative flex-1">
                    <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-mocha" />
                    <Input data-testid="track-input" value={num} onChange={(e) => setNum(e.target.value)} placeholder="SP-1A2B3C4D" className="h-12 rounded-full border-caramel/15 bg-white pl-11 focus-visible:ring-caramel" />
                </div>
                <Button type="submit" disabled={loading} data-testid="track-submit" className="h-12 rounded-full bg-caramel px-7 text-cream-50 hover:bg-caramel-dark">Track</Button>
            </form>

            {order && (
                <div className="mt-10 rounded-3xl border border-caramel/10 bg-white p-7 shadow-warm" data-testid="track-result">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-mocha">Order</p>
                            <p className="font-serif text-2xl text-roast">{order.order_number}</p>
                        </div>
                        <span className="rounded-full bg-banana/25 px-3 py-1 text-sm font-medium capitalize text-caramel">{order.fulfillment}</span>
                    </div>

                    <div className="mt-8 space-y-5">
                        {STEPS.map((s, i) => (
                            <div key={s} className="flex items-center gap-4">
                                <div className={`grid h-9 w-9 place-items-center rounded-full ${i <= currentStep ? "bg-caramel text-cream-50" : "bg-cream-200 text-mocha"}`}>
                                    {i < currentStep ? <CheckCircle2 size={18} /> : i === currentStep ? <Clock size={18} /> : <Truck size={16} />}
                                </div>
                                <span className={`${i <= currentStep ? "font-medium text-roast" : "text-mocha"}`}>{s}</span>
                            </div>
                        ))}
                    </div>

                    <div className="mt-8 border-t border-caramel/10 pt-5 text-sm">
                        {order.fulfillment_date && <div className="flex justify-between"><span className="text-mocha">Scheduled date</span><span className="text-roast">{order.fulfillment_date}</span></div>}
                        <div className="mt-1 flex justify-between"><span className="text-mocha">Total</span><span className="font-semibold text-roast">${order.total.toFixed(2)}</span></div>
                    </div>
                </div>
            )}

            <p className="mt-8 text-center text-sm text-mocha">Have an account? <Link to="/account" className="font-medium text-caramel hover:underline">See all your orders</Link></p>
        </div>
    );
}
